import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

type HomePageCtaCardProps = Readonly<{
  illustration: ReactNode;
  title: string;
  description: string;
  ctaLabel: string;
  ctaPath: string;
}>;

export const HomePageCtaCard = ({
  illustration,
  title,
  description,
  ctaLabel,
  ctaPath,
}: HomePageCtaCardProps) => {
  const navigate = useNavigate();

  return (
    <Box sx={containerSx}>
      <Box sx={illustrationSx}>{illustration}</Box>
      <Box sx={dividerSx} />
      <Box sx={contentSx}>
        <Box sx={textSx}>
          <Typography variant="text-sm-semibold">{title}</Typography>
          <Typography variant="text-sm-regular" sx={descriptionSx}>
            {description}
          </Typography>
        </Box>
        <Button
          id="home-cta-card-btn"
          hierarchy="Primary"
          size="xl"
          endIcon={<AppIcon iconName="ArrowRight" diameter="20px" color="Colors/Base/White" />}
          onClick={() => navigate(ctaPath)}
          sx={ctaButtonSx}
        >
          {ctaLabel}
        </Button>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  containerType: 'inline-size',
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

const illustrationSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  maxHeight: 375,
  overflow: 'hidden',
  '& svg, & img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
};

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  px: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-lg'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  '@container (min-width: 400px)': {
    gap: backrs.spacing['spacing-md'],
    px: backrs.spacing['spacing-2xl'],
    py: backrs.spacing['spacing-xl'],
  },
  '@container (min-width: 600px)': {
    px: backrs.spacing['spacing-3xl'],
    py: backrs.spacing['spacing-2xl'],
  },
  '@container (max-width: 700px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: backrs.spacing['spacing-lg'],
  },
});

const textSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const ctaButtonSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/500'],
  border: `2px solid ${backrs.colors['Colors/Base/Black']}`,
  flexShrink: 0,
  '@container (max-width: 700px)': {
    width: '100%',
  },
});
